import React, { Suspense, useMemo } from 'react';
import { HelpCircle } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import { lazyWithRetry } from '../utils/lazyWithRetry';

const iconComponentCache = new Map();

/** PascalCase / legacy names → lucide dynamic import keys */
const ICON_ALIASES = {
  Building2: 'building-2',
  CheckCircle2: 'check-circle-2',
  CheckCircle: 'check-circle',
  XCircle: 'x-circle',
  AlertCircle: 'alert-circle',
  LockAlert: 'lock-keyhole',
  Grid2x2: 'grid-2x2',
  Grid3X3: 'grid-3x3',
  ExchangeAlt: 'arrow-left-right',
};

const toLucideIconKey = (value = '') => {
  if (!value) return '';

  const gridMatch = value.match(/^Grid(\d+)[Xx](\d+)$/i);
  if (gridMatch) {
    return `grid-${gridMatch[1]}x${gridMatch[2]}`;
  }

  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .replace(/([A-Za-z])(\d+)$/g, '$1-$2')
    .toLowerCase();
};

const resolveLucideIconKey = (name = '') => {
  if (ICON_ALIASES[name]) return ICON_ALIASES[name];

  const candidates = [
    toLucideIconKey(name),
    name.replace(/([a-z])(\d)/g, '$1-$2').toLowerCase(),
    name.toLowerCase(),
  ];

  return candidates.find((key) => key && dynamicIconImports[key]) || candidates[0];
};

const getLazyIcon = (name) => {
  const key = resolveLucideIconKey(name);
  if (!key || !dynamicIconImports[key]) return null;

  if (!iconComponentCache.has(key)) {
    iconComponentCache.set(
      key,
      lazyWithRetry(() =>
        dynamicIconImports[key]().then((mod) => ({ default: mod.default })),
      ),
    );
  }

  return iconComponentCache.get(key);
};

const IconFallback = ({ size, className }) => (
  <span
    className={`inline-block shrink-0 rounded-sm bg-muted/40 animate-pulse ${className || ''}`}
    style={{ width: size, height: size }}
    aria-hidden
  />
);

function Icon({
  name,
  size = 24,
  color = 'currentColor',
  className = '',
  strokeWidth = 2,
  ...props
}) {
  const LazyIcon = useMemo(() => getLazyIcon(name), [name]);

  if (!LazyIcon) {
    return (
      <HelpCircle
        size={size}
        color="gray"
        strokeWidth={strokeWidth}
        className={className}
        {...props}
      />
    );
  }

  return (
    <Suspense fallback={<IconFallback size={size} className={className} />}>
      <LazyIcon
        size={size}
        color={color}
        strokeWidth={strokeWidth}
        className={className}
        {...props}
      />
    </Suspense>
  );
}

export default Icon;
